import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, EnvelopeSimple, Phone, WhatsappLogo, PencilSimple, Trash, Users } from "@phosphor-icons/react";
import api from "@/lib/api";
import { eur, fdate } from "@/lib/format";
import { PageHeader, EmptyState, DemoBadge } from "@/components/common";
import ContactForm from "@/components/ContactForm";
import ContactMatches from "@/components/ContactMatches";
import { ScoreChip, WhyScore } from "@/components/ScoreCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from 'react-i18next'

export default function Contacts() {
  const { t } = useTranslation()
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [type, setType] = useState(params.get("type") || "all");
  const dashTypes = (params.get("types") || "").split(",").map((s) => s.trim()).filter(Boolean);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [matchFor, setMatchFor] = useState(null);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });

  useEffect(() => { if (params.get("new") === "1") { setShowForm(true); setParams({}); } }, [params, setParams]);

  const filters = {};
  if (!dashTypes.length && type !== "all") filters.contact_type = type;
  if (q) filters.q = q;

  const { data: allContacts = [], isLoading } = useQuery({
    queryKey: ["contacts", filters],
    queryFn: async () => (await api.get("/contacts", { params: filters })).data,
  });
  const contacts = dashTypes.length ? allContacts.filter((c) => dashTypes.includes(c.contact_type)) : allContacts;

  const { data: scored = [] } = useQuery({
    queryKey: ["scoring-contacts"],
    queryFn: async () => (await api.get("/scoring/contacts")).data,
  });
  const scoreById = React.useMemo(() => {
    const m = {};
    scored.forEach((s) => { m[s.contact.id] = s.score; });
    return m;
  }, [scored]);

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/contacts", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); setShowForm(false); toast.success("Contact created"); },
  });
  const update = useMutation({
    mutationFn: async ({ id, payload }) => (await api.put(`/contacts/${id}`, payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["contacts"] }); setEditing(null); toast.success("Contact updated"); },
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/contacts/${id}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["contacts"] });
      if (data?.pending_approval) toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      else toast.success(t("contactDeleted", "Contact deleted"));
    },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  return (
    <div>
      <PageHeader
        title="Contacts"
        subtitle={t('lengthContactsInYourNetwork', '{{length}} contacts in your network', { length: contacts.length })}
        actions={
          <Button data-testid="add-contact-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <Plus size={16} className="mr-1.5" /> {t('addContact2', 'Add Contact')}
          </Button>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="contact-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('searchNameEmailCompany', 'Search name, email, company…')} className="pl-10 rounded-sm bg-white" />
        </div>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="filter-contact-type" className="w-full sm:w-56 rounded-sm bg-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white max-h-64">
            <SelectItem value="all">{t('allTypes', 'All types')}</SelectItem>
            {meta?.contact_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : contacts.length === 0 ? (
        <EmptyState icon={Users} title={t('noContactsYet', 'No contacts yet')} subtitle="Add owners, buyers, investors and partners." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {contacts.map((c) => (
            <div key={c.id} data-testid={`contact-card-${c.id}`} className="bg-white border border-slate-200 rounded-sm p-5 hover:-translate-y-[2px] transition-transform shadow-[0_8px_30px_rgba(11,37,69,0.04)]">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-sm bg-[#0B2545] text-white flex items-center justify-center font-semibold flex-shrink-0 overflow-hidden">
                  {c.photo ? <img src={c.photo} alt="" className="w-full h-full object-cover" /> : c.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-slate-900 truncate">{c.name}</h3>
                    {c.is_demo && <DemoBadge />}
                  </div>
                  <div className="text-xs text-slate-500">{c.position ? `${c.position}, ` : ""}{c.company || "—"}</div>
                  <span className="inline-block mt-1.5 text-[10px] uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded-sm">{c.contact_type}</span>
                </div>
              </div>
              {(c.min_budget || c.max_budget) && (
                <div className="text-xs text-slate-500 mt-3">{t('budget', 'Budget:')} {eur(c.min_budget)} – {eur(c.max_budget)}</div>
              )}
              {c.preferred_locations && <div className="text-xs text-slate-400 mt-1">{t('prefersPreferred_locations', 'Prefers: {{preferred_locations}}', { preferred_locations: c.preferred_locations })}</div>}
              {scoreById[c.id] && (
                <div className="mt-3 pt-3 border-t border-slate-100">
                  <ScoreChip score={scoreById[c.id]} testid={`contact-score-${c.id}`} />
                  <WhyScore score={scoreById[c.id]} testid={`contact-why-${c.id}`} />
                </div>
              )}
              <div className="flex items-center gap-3 mt-4 pt-3 border-t border-slate-100">
                {c.email && <a href={`mailto:${c.email}`} className="text-slate-400 hover:text-[#C5A059]" title={c.email}><EnvelopeSimple size={18} /></a>}
                {c.mobile && <a href={`tel:${c.mobile}`} className="text-slate-400 hover:text-[#C5A059]"><Phone size={18} /></a>}
                {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-emerald-600"><WhatsappLogo size={18} /></a>}
                <div className="flex-1" />
                {["Buyer", "Investor", "Fund", "Family office", "Developer"].includes(c.contact_type) && (
                  <button onClick={() => setMatchFor(c)} data-testid={`view-matches-${c.id}`} className="text-xs font-medium text-[#C5A059] hover:underline mr-1">{t('matching.viewMatches', 'View matches')}</button>
                )}
                <button onClick={() => setEditing(c)} data-testid={`edit-contact-${c.id}`} className="text-slate-400 hover:text-[#0B2545]"><PencilSimple size={16} /></button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="text-slate-400 hover:text-red-600" data-testid={`delete-contact-${c.id}`}><Trash size={16} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white rounded-sm">
                    <AlertDialogHeader><AlertDialogTitle>{t('deleteName', 'Delete {{name}}?', { name: c.name })}</AlertDialogTitle></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">{t('cancel', 'Cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove.mutate(c.id)} className="rounded-sm bg-red-600 hover:bg-red-700">{t('delete', 'Delete')}</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && <ContactForm open={showForm} onOpenChange={setShowForm} meta={meta} onSubmit={(payload) => create.mutate(payload)} />}
      {editing && <ContactForm open={!!editing} onOpenChange={(o) => !o && setEditing(null)} meta={meta} initial={editing} onSubmit={(payload) => update.mutate({ id: editing.id, payload })} />}
      {matchFor && <ContactMatches contact={matchFor} open={!!matchFor} onOpenChange={(o) => !o && setMatchFor(null)} />}
    </div>
  );
}
